import React from 'react'
import Table from '@/components/table/Table'
import Row from '@/components/table/Row'
import PopupDialog from '@/components/popupDialog/PopupDialog'
import AddField from '@/components/addField/AddField'
import {
  useLazyGetPlacesQuery,
  useLazyAddPlaceQuery,
  useLazyDeletePlaceQuery,
  useLazyUpdatePlaceQuery,
  useLazyUpdateSortOrderQuery,
  IPlace,
  IPlaceCreate,
  IPlaceUpdate,
} from '@/store/apis/places'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

interface IState {
  selectedPopup?: 'ADD' | 'DELETE'
  deletePlace?: IPlace
  addPlace?: IPlaceCreate
  editPlace?: IPlaceUpdate
}

const Places: React.FC = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const [getPlacesQuery, { data: placesData, isLoading: isGettingPlaces }] =
    useLazyGetPlacesQuery()
  const [addPlaceQuery, { isFetching: isAddingPlace }] = useLazyAddPlaceQuery()
  const [deletePlaceQuery, { isFetching: isDeletingPlace }] =
    useLazyDeletePlaceQuery()
  const [updatePlaceQuery, { isFetching: isUpdatingPlace }] =
    useLazyUpdatePlaceQuery()
  const [updateSortOrder] = useLazyUpdateSortOrderQuery()
  const [state, setState] = React.useState<IState>({})
  const [places, setPlaces] = React.useState<IPlace[]>([])
  const schemaId = Number(urlParams.schemaId)

  React.useEffect(() => {
    if (placesData) {
      setPlaces(placesData.items)
    }
  }, [placesData])

  React.useEffect(() => {
    getPlacesQuery({ schemaId })
  }, [getPlacesQuery])

  const handleAdd = async (): Promise<void> => {
    if (state.addPlace) {
      await addPlaceQuery({ schemaId, ...state.addPlace })
      await getPlacesQuery({ schemaId })
      setState({
        ...state,
        addPlace: undefined,
        selectedPopup: undefined,
      })
    }
  }

  const handleDelete = async (id: number): Promise<void> => {
    await deletePlaceQuery({ schemaId, id })
    await getPlacesQuery({ schemaId })
    setState({
      ...state,
      selectedPopup: undefined,
      deletePlace: undefined,
    })
  }

  const handleEdit = async (id: number): Promise<void> => {
    if (state.editPlace) {
      await updatePlaceQuery({ schemaId, id, ...state.editPlace })
      await getPlacesQuery({ schemaId })
      setState({ ...state, editPlace: undefined })
    }
  }

  const handleDragEnd = async (dropResult: DropResult) => {
    if (dropResult.destination && placesData) {
      const tempData = [...placesData.items]
      const item = tempData.splice(dropResult.source.index, 1)[0]
      tempData.splice(dropResult.destination.index, 0, item)
      const sortIds = tempData.map((data, index) => ({
        id: data.id,
        sortId: index,
      }))
      setPlaces(tempData)
      await updateSortOrder({ schemaId, sortIds })
      await getPlacesQuery({ schemaId })
    }
  }

  const { selectedPopup, deletePlace, addPlace, editPlace } = state

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tableBodyDropArea">
          {(dropProvider) => (
            <Table
              dropRef={dropProvider.innerRef}
              dropProps={dropProvider.droppableProps}
              headerLabels={[t('headers.name')]}
              isLoading={isGettingPlaces}
              isEditing={!!editPlace}
              isAddOpen={selectedPopup === 'ADD'}
              onAddOpen={() =>
                setState({
                  ...state,
                  selectedPopup: 'ADD',
                  addPlace: {
                    name: '',
                  },
                })
              }
            >
              {places?.map((place, index) => (
                <Draggable
                  key={place.name}
                  draggableId={place.name}
                  index={index}
                >
                  {(dragProvider) => (
                    <Row
                      key={place.name}
                      dragProps={dragProvider.draggableProps}
                      dragHandleProps={dragProvider.dragHandleProps}
                      dragRef={dragProvider.innerRef}
                      isLoading={isUpdatingPlace}
                      disableButtons={!!editPlace}
                      onEditOpen={() =>
                        setState({ ...state, editPlace: place })
                      }
                      onEditSuccess={() => handleEdit(place.id)}
                      onEditAbort={() =>
                        setState({ ...state, editPlace: undefined })
                      }
                      onDeleteOpen={() =>
                        setState({
                          ...state,
                          selectedPopup: 'DELETE',
                          deletePlace: place,
                        })
                      }
                      columns={{
                        normal: [place.name],
                        edit: editPlace && [
                          <AddField
                            value={editPlace.name}
                            required={true}
                            regex={/^$/}
                            onChange={(newValue) =>
                              setState({
                                ...state,
                                editPlace: { ...editPlace, name: newValue },
                              })
                            }
                          />,
                        ],
                      }}
                    />
                  )}
                </Draggable>
              ))}
              {dropProvider.placeholder}
              {selectedPopup === 'ADD' && addPlace && (
                <Row
                  isLoading={isAddingPlace}
                  onAddSuccess={() => handleAdd()}
                  onAddAbort={() =>
                    setState({
                      ...state,
                      addPlace: undefined,
                      selectedPopup: undefined,
                    })
                  }
                  columns={{
                    normal: [
                      '',
                      <AddField
                        value={addPlace.name}
                        required={true}
                        regex={/^$/}
                        onChange={(newValue) =>
                          setState({
                            ...state,
                            addPlace: { ...addPlace, name: newValue },
                          })
                        }
                      />,
                    ],
                  }}
                />
              )}
            </Table>
          )}
        </Droppable>
      </DragDropContext>
      <PopupDialog
        isOpen={selectedPopup === 'DELETE'}
        title={
          <>
            {t('popups.removePlaceTitle')}: <b>{deletePlace?.name}</b>
          </>
        }
        content={t('popups.removePlaceDescription')}
        isLoading={isDeletingPlace}
        onClose={() => setState({ ...state, selectedPopup: undefined })}
        onConfirm={() =>
          deletePlace?.id ? handleDelete(deletePlace.id) : null
        }
      />
    </>
  )
}

export default Places
